import { Controller } from "@hotwired/stimulus"
import {
  calculateItemDiscount,
  discountPerUnit,
  roundTo2,
  UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
} from "../../utils/calculation.util"
import {
  clearPercentField,
  concealLockReason,
  parseDiscountPercent,
  revealLockReason,
  syncGrossAmountLock,
  validateAndClampPercent
} from "../../utils/discount.util"
import { formatNumber, getCurrencyDecimals, getUserLocale, parseInputNumber } from "../../utils/number-format.util"
import { formatDecimal, serverNumberFormatFor } from "../../utils/server-format.util"

// Connects to data-controller="documents--item-discount"
export default class extends Controller {
  //       into documents--item controller and this will act like parent controller
  static outlets = ["documents--item"]

  static targets = [
    "percent",
    "price",
    "totalItemDiscount",
    "description",
    "typeToggle",
    "perUnitDisplay",
    "togglePercent",
    "toggleAmount",
    // Why the EUR toggle is locked in gross mode, always hidden in the row. An expense's line only.
    "lockReason"
  ]

  static values = {
    defaultDescription: String,
    discountType: { type: String, default: "percentage" },
    currency: { type: String, default: "EUR" },
    // Why the EUR toggle is locked in gross mode, shown as its title. An expense's line only.
    grossModeTitle: String
  }

  connect() {
    // Detect initial mode from server-rendered values:
    // If price has a value > 0 and percent is empty/zero, start in amount mode.
    if (this.hasPriceTarget && this.hasPercentTarget) {
      const locale = getUserLocale()
      const priceVal = parseInputNumber(this.priceTarget, locale) || 0
      const pctVal = parseInputNumber(this.percentTarget, locale) || 0
      if (priceVal > 0 && pctVal === 0) {
        this.discountTypeValue = "amount"
      }
    }

    // A Turbo snapshot taken while a refused tap showed the reason restores it shown, with no timer
    // left to hide it (revealLockReason).
    if (this.hasLockReasonTarget) this.lockReasonTarget.hidden = true

    this.#syncVisibility()
    this.syncGrossMode()
    this.#computeInitialDisplayValues()

    // Keep the [%|EUR] toggle button in sync with the document's currency
    // selector so swapping EUR → CZK live updates the chip text.
    this.documentCurrencySelect = document.querySelector("#currency")
    if (this.documentCurrencySelect) {
      this.currencyValue = this.documentCurrencySelect.value || this.currencyValue
      this._onDocumentCurrencyChange = (event) => {
        this.currencyValue = event.target.value
        this.#syncCurrencyLabel()
        this.recalculateTotalDiscount()
        this.#updateDescription()
      }
      this.documentCurrencySelect.addEventListener("change", this._onDocumentCurrencyChange)
    }
    this.#syncCurrencyLabel()
  }

  // The line's item controller may connect after this row; its mode is read once it is there.
  documentsItemOutletConnected() {
    this.syncGrossMode()
  }

  disconnect() {
    if (this.documentCurrencySelect && this._onDocumentCurrencyChange) {
      this.documentCurrencySelect.removeEventListener("change", this._onDocumentCurrencyChange)
      this._onDocumentCurrencyChange = null
    }
    concealLockReason(this.hasLockReasonTarget ? this.lockReasonTarget : null)
  }

  // Public API: gross_per_rate takes percentage discounts only (spec NG5), so while the line is
  // calculated per gross (documents--item#isGrossMode) its EUR toggle is locked and
  // toggleDiscountType refuses the amount. The document's block does the same
  // (documents--discount#syncGrossMode); the lock is syncGrossAmountLock's.
  syncGrossMode() {
    const backToPercentage = syncGrossAmountLock({
      gross: this.#isGrossLine(),
      toggle: this.hasToggleAmountTarget ? this.toggleAmountTarget : null,
      title: this.grossModeTitleValue,
      reason: this.hasLockReasonTarget ? this.lockReasonTarget : null,
      isAmount: this.discountTypeValue === "amount",
      amountInput: this.hasPriceTarget ? this.priceTarget : null
    })

    if (backToPercentage) {
      this.discountTypeValue = "percentage"
      this.#syncVisibility()
    }
  }

  // Mirror the document currency onto the [%|EUR] amount-mode toggle button.
  #syncCurrencyLabel() {
    if (!this.hasToggleAmountTarget) return
    this.toggleAmountTarget.textContent = (this.currencyValue || "EUR").toUpperCase()
  }

  // User clicked one of the toggle buttons [% | EUR]
  toggleDiscountType(event) {
    const requestedType = event.currentTarget.dataset.discountType
    if (requestedType === this.discountTypeValue) return // already in this mode
    // gross_per_rate: percentages only. The refused tap says why (a touch screen shows no title).
    if (requestedType === "amount" && this.#isGrossLine()) {
      revealLockReason(this.hasLockReasonTarget ? this.lockReasonTarget : null)
      return
    }

    const { unitPrice, locale } = this.#initializeConstants()

    if (requestedType === "amount") {
      // % -> amount: compute per-unit from percentage, pre-fill price input
      const pct = parseDiscountPercent(this.percentTarget, locale)
      const perUnit = unitPrice > 0 ? discountPerUnit(unitPrice, { percent: pct }) : 0
      this.priceTarget.value =
        perUnit > 0
          ? formatNumber(perUnit, locale, {
              maximumFractionDigits: UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
            })
          : ""
      this.percentTarget.value = "" // clear percentage (mutual exclusivity)
    } else {
      // amount -> %: compute percentage from per-unit, pre-fill percent input
      const perUnit = parseInputNumber(this.priceTarget, locale) || 0
      const pct = unitPrice > 0 ? roundTo2((perUnit / unitPrice) * 100) : 0
      this.percentTarget.value =
        pct > 0
          ? formatNumber(pct, locale, {
              style: "decimal",
              minimumFractionDigits: 0,
              maximumFractionDigits: 2
            })
          : ""
      this.priceTarget.value = "" // clear amount (mutual exclusivity)
    }

    this.discountTypeValue = requestedType
    this.#syncVisibility()
    this.syncGrossMode()
    this.#updateDisplayValues()
    this.#updateDescription()
    this.documentsItemOutlet.onDiscountItemChange()
  }

  onDiscountPercentChange() {
    const { unitPrice, quantity, locale } = this.#initializeConstants()
    this.#setRangeForDiscount(unitPrice, quantity, locale)
    if (this.#isUnitPriceZero(unitPrice)) {
      return
    }
    // Parse and validate percentage value
    let discountPercent = parseDiscountPercent(this.percentTarget, locale)
    discountPercent = validateAndClampPercent(this.percentTarget, discountPercent, locale)

    // Mutual exclusivity: clear the amount field
    if (this.hasPriceTarget) {
      this.priceTarget.value = ""
    }

    this.#renderDiscount(discountPerUnit(unitPrice, { percent: discountPercent }), quantity)

    this.#updateDescription()
    this.documentsItemOutlet.onDiscountItemChange()
  }

  // User typed per-unit amount directly in amount mode
  onDiscountAmountChange() {
    const { unitPrice, quantity, locale } = this.#initializeConstants()

    if (this.#isUnitPriceZero(unitPrice)) {
      if (this.hasPriceTarget) {
        this.priceTarget.value = ""
      }
      if (this.hasPercentTarget) {
        this.percentTarget.value = ""
      }
      this.syncGrossMode()
      this.#updateDescription()
      this.documentsItemOutlet.onDiscountItemChange()
      return
    }

    let perUnit = parseInputNumber(this.priceTarget, locale) || 0

    // Clamp: per-unit discount cannot exceed unit price (parallels validateAndClampPercent)
    if (perUnit > unitPrice && unitPrice > 0) {
      perUnit = unitPrice
      this.priceTarget.value = formatNumber(unitPrice, locale, {
        maximumFractionDigits: UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
      })
    } else if (perUnit < 0) {
      perUnit = 0
      this.priceTarget.value = formatNumber(0, locale, {
        maximumFractionDigits: UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
      })
    }

    // Mutual exclusivity: clear the percentage field
    if (this.hasPercentTarget) {
      this.percentTarget.value = ""
    }

    this.#renderDiscount(discountPerUnit(unitPrice, { amount: perUnit }), quantity)

    this.syncGrossMode()
    this.#updateDescription()
    this.documentsItemOutlet.onDiscountItemChange()
  }

  #setRangeForDiscount(_unitPrice, _quantity, locale) {
    // Validate and clamp percentage to valid range (0-100)
    const discountPercent = parseDiscountPercent(this.percentTarget, locale)
    validateAndClampPercent(this.percentTarget, discountPercent, locale)
  }

  #initializeConstants() {
    const locale = getUserLocale()
    const unitPrice = parseInputNumber(this.documentsItemOutlet.unitPriceTarget, locale) || 0
    const quantity = parseInputNumber(this.documentsItemOutlet.quantityTarget, locale) || 0
    return { unitPrice, quantity, locale }
  }

  #isUnitPriceZero(unitPrice) {
    if (unitPrice === 0) {
      const locale = getUserLocale()
      clearPercentField(this.percentTarget, locale)
      if (this.hasTotalItemDiscountTarget) {
        this.#renderTotal(0)
      }
      if (this.hasPerUnitDisplayTarget) {
        this.perUnitDisplayTarget.innerText = ""
      }
      return true
    }
    return false
  }

  // Public API: Show discount row and clear all fields (called from item controller)
  show() {
    const locale = getUserLocale()
    // Reset to percentage mode by default when showing fresh
    this.discountTypeValue = "percentage"

    // Clear all discount values when showing (to start fresh)
    if (this.hasPercentTarget) {
      clearPercentField(this.percentTarget, locale)
    }
    if (this.hasPriceTarget) {
      this.priceTarget.value = ""
    }
    if (this.hasTotalItemDiscountTarget) {
      this.#renderTotal(0)
    }
    if (this.hasPerUnitDisplayTarget) {
      this.perUnitDisplayTarget.innerText = ""
    }
    if (this.hasDescriptionTarget) {
      // Set default description if field is empty
      if (!this.descriptionTarget.value || this.descriptionTarget.value.trim() === "") {
        this.descriptionTarget.value = this.hasDefaultDescriptionValue ? this.defaultDescriptionValue : ""
      }
    }

    this.#syncVisibility()
    this.syncGrossMode()
    this.element.classList.remove("d-none")
    // Focus on discount description field
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.focus()
    }
  }

  hideDiscount() {
    const locale = getUserLocale()
    // Clear all discount fields first
    // Set to empty string so it's not submitted (empty string will be nil in Rails)
    if (this.hasPercentTarget) {
      clearPercentField(this.percentTarget, locale)
      this.percentTarget.value = ""
    }
    if (this.hasPriceTarget) {
      // Set to empty string so it's not submitted (empty string will be nil in Rails)
      this.priceTarget.value = ""
    }
    if (this.hasTotalItemDiscountTarget) {
      this.#renderTotal(0)
    }
    if (this.hasPerUnitDisplayTarget) {
      this.perUnitDisplayTarget.innerText = ""
    }
    // Clear description field if it exists
    if (this.hasDescriptionTarget) {
      this.descriptionTarget.value = ""
    }
    this.syncGrossMode()
    this.element.classList.add("d-none")
    // Trigger recalculation to update item totals after clearing discount
    // Call onDiscountItemChange to notify item controller (don't call onDiscountPercentChange as fields are empty)
    requestAnimationFrame(() => {
      this.documentsItemOutlet.onDiscountItemChange()
    })
  }

  // Public API: Recalculate total discount when quantity or unit price changes
  recalculateTotalDiscount() {
    if (!this.hasTotalItemDiscountTarget) return

    const { unitPrice, quantity } = this.#initializeConstants()
    this.#renderDiscount(this.#discountPerUnit(unitPrice), quantity)
  }

  // Public API: the line's percentage as typed, 0 when blank. A line calculated per gross takes
  // this alone: its discount is a share of the gross price, and an amount (refused by the
  // server) plays no part.
  getDiscountPercent() {
    return this.hasPercentTarget ? parseDiscountPercent(this.percentTarget, getUserLocale()) : 0
  }

  // Public API: the line's per-unit discount exactly as the server computes it
  // from the submitted fields (ItemCalculatable#discount_per_unit) — what the
  // totals subtract from the unit price before the single BT-131 rounding.
  // Signed like the server: a percentage of a negative unit price is negative.
  getDiscountPerUnit() {
    const { unitPrice } = this.#initializeConstants()
    return this.#discountPerUnit(unitPrice)
  }

  // --- Private ---

  #isGrossLine() {
    return this.hasDocumentsItemOutlet && this.documentsItemOutlet.isGrossMode()
  }

  // Auto-update description with discount value, e.g. "Zľava na položku 15 %" or "Zľava na položku 5,00 EUR"
  // Only updates if description is empty, matches the default, or was previously auto-generated.
  #updateDescription() {
    if (!this.hasDescriptionTarget || !this.hasDefaultDescriptionValue) return

    const base = this.defaultDescriptionValue
    const current = this.descriptionTarget.value.trim()

    // Only auto-update if empty, matches default, or starts with default (previously auto-generated)
    if (current !== "" && current !== base && !current.startsWith(base)) return

    const locale = getUserLocale()

    if (this.discountTypeValue === "percentage") {
      const pct = parseDiscountPercent(this.percentTarget, locale)
      if (pct > 0) {
        this.descriptionTarget.value = `${base} ${formatNumber(pct, locale, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} %`
      } else {
        this.descriptionTarget.value = base
      }
    } else {
      const perUnit = parseInputNumber(this.priceTarget, locale) || 0
      if (perUnit > 0) {
        const currency = this.currencyValue || "EUR"
        this.descriptionTarget.value = `${base} ${formatNumber(perUnit, locale, { maximumFractionDigits: UNIT_PRICE_MAXIMUM_FRACTION_DIGITS })} ${currency}`
      } else {
        this.descriptionTarget.value = base
      }
    }
  }

  #syncVisibility() {
    const isPercentage = this.discountTypeValue === "percentage"

    // Toggle input visibility
    if (this.hasPercentTarget) {
      this.percentTarget.classList.toggle("d-none", !isPercentage)
    }
    if (this.hasPriceTarget) {
      this.priceTarget.classList.toggle("d-none", isPercentage)
    }

    // Toggle button active states
    if (this.hasTogglePercentTarget) {
      this.togglePercentTarget.classList.toggle("btn-primary-light", isPercentage)
      this.togglePercentTarget.classList.toggle("btn-secondary", !isPercentage)
    }
    if (this.hasToggleAmountTarget) {
      this.toggleAmountTarget.classList.toggle("btn-primary-light", !isPercentage)
      this.toggleAmountTarget.classList.toggle("btn-secondary", isPercentage)
    }
  }

  #currencyDecimals() {
    return getCurrencyDecimals(this.currencyValue || this.documentCurrencySelect?.value || "EUR")
  }

  // A discount PRINTED in the row (per unit, item total) — not an input:
  // replayed from the form's data-server-number-format like the totals
  // beside it, Intl only when the page embeds no server data. Two decimals
  // at least, as formatNumber's decimal default.
  #formatDisplay(value, { minimumFractionDigits = 2, maximumFractionDigits = 2 } = {}) {
    const digits = {
      minimumFractionDigits,
      maximumFractionDigits: Math.max(minimumFractionDigits, maximumFractionDigits)
    }
    const numberFormat = serverNumberFormatFor(this.element)
    if (numberFormat) return formatDecimal(value, numberFormat, digits)
    return formatNumber(value, getUserLocale(), digits)
  }

  // The per-unit discount the server derives from these two fields: an amount
  // > 0 wins, else the percentage (ItemCalculatable#discount_per_unit).
  #discountPerUnit(unitPrice) {
    const locale = getUserLocale()
    const percent = this.hasPercentTarget ? parseDiscountPercent(this.percentTarget, locale) : 0
    const amount = this.hasPriceTarget ? parseInputNumber(this.priceTarget, locale) || 0 : 0
    return discountPerUnit(unitPrice, { percent, amount })
  }

  // Prints a line's per-unit discount and its discount total.
  #renderDiscount(perUnit, quantity) {
    if (this.hasPerUnitDisplayTarget) {
      this.perUnitDisplayTarget.innerText =
        perUnit !== 0
          ? this.#formatDisplay(-Math.abs(perUnit), {
              maximumFractionDigits: UNIT_PRICE_MAXIMUM_FRACTION_DIGITS
            })
          : ""
    }
    if (this.hasTotalItemDiscountTarget) {
      this.#renderTotal(calculateItemDiscount(quantity, perUnit, this.#currencyDecimals()))
    }
  }

  // Prints the item's discount total as a negative amount whatever its sign —
  // a credit-note line (negative quantity) keeps its discount — as the PDF
  // does (documents/pdf/create.html.erb, `item_discount_total`). Display only:
  // the totals take the line net from getDiscountPerUnit.
  #renderTotal(totalDiscount) {
    const magnitude = Math.abs(totalDiscount)
    this.totalItemDiscountTarget.innerText =
      magnitude > 0 ? this.#formatDisplay(-magnitude, this.#currencyFractionOptions()) : ""
  }

  #currencyFractionOptions() {
    const decimals = this.#currencyDecimals()
    return { minimumFractionDigits: decimals, maximumFractionDigits: decimals }
  }

  #computeInitialDisplayValues() {
    if (!this.hasTotalItemDiscountTarget) return

    const { unitPrice, quantity } = this.#initializeConstants()
    this.#renderDiscount(this.#discountPerUnit(unitPrice), quantity)
  }

  #updateDisplayValues() {
    const { unitPrice, quantity } = this.#initializeConstants()
    this.#renderDiscount(this.#discountPerUnit(unitPrice), quantity)
  }
}
